import React, { useCallback, useEffect, useState } from 'react';
import { Download, Loader2, RefreshCw, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { listGoogleAutojoinEvents } from '@/services/admin';

const MATCH_LABEL = { google_policy: 'Google policy', tenant_domain: 'Tenant domain' };
const KIND_LABEL = { new_user: 'New user', existing_user: 'Existing user', reactivated: 'Reactivated' };
const COLUMNS = ['created_at', 'email', 'full_name', 'role', 'matched_by', 'domain', 'user_kind'];

const toCsv = (rows) => {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  return [COLUMNS.join(','), ...rows.map((r) => COLUMNS.map((c) => esc(r[c])).join(','))].join('\n');
};

const downloadCsv = (rows) => {
  const blob = new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `google-autojoin-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const EventRow = ({ e }) => (
  <tr className="border-t text-xs" data-testid={`autojoin-event-${e.id}`}>
    <td className="py-2 pr-3 whitespace-nowrap text-slate-600">{new Date(e.created_at).toLocaleString()}</td>
    <td className="py-2 pr-3">
      <div className="font-medium text-slate-800 break-all">{e.email}</div>
      {e.full_name && <div className="text-slate-500">{e.full_name}</div>}
    </td>
    <td className="py-2 pr-3"><Badge variant="outline">{(e.role || '').replace('_', ' ')}</Badge></td>
    <td className="py-2 pr-3 whitespace-nowrap">{MATCH_LABEL[e.matched_by] || e.matched_by} · @{e.domain}</td>
    <td className="py-2 whitespace-nowrap text-slate-600">{KIND_LABEL[e.user_kind] || e.user_kind}</td>
  </tr>
);

const AutojoinActivityLog = ({ tenantId }) => {
  const [events, setEvents] = useState(null);
  const [q, setQ] = useState('');

  const load = useCallback(async (query) => {
    setEvents(null);
    try {
      const res = await listGoogleAutojoinEvents(tenantId, query);
      setEvents(res.events || []);
    } catch (err) {
      setEvents([]);
      toast.error(err.response?.data?.detail || 'Failed to load auto-join activity');
    }
  }, [tenantId]);

  useEffect(() => {
    const t = setTimeout(() => load(q), 300);
    return () => clearTimeout(t);
  }, [q, load]);

  return (
    <div className="space-y-3 border-t pt-5" data-testid="autojoin-activity-log">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-medium">Auto-join activity</div>
          <p className="text-xs text-muted-foreground">Last 50 people added to this tenant through Google sign-in.</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => load(q)} data-testid="autojoin-refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="outline" disabled={!events?.length}
            onClick={() => downloadCsv(events)} data-testid="autojoin-export-csv-btn">
            <Download className="w-4 h-4 mr-1" />CSV
          </Button>
        </div>
      </div>
      <div className="relative max-w-sm">
        <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
        <Input className="pl-8" placeholder="Search email, name, role, domain" value={q}
          onChange={(e) => setQ(e.target.value)} data-testid="autojoin-search-input" />
      </div>
      {events === null ? (
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" data-testid="autojoin-loading" />
      ) : events.length === 0 ? (
        <p className="text-xs text-muted-foreground py-3" data-testid="autojoin-empty">No auto-joins yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left min-w-[560px]" data-testid="autojoin-table">
            <thead>
              <tr className="text-xs text-slate-500">
                <th className="pb-2 pr-3">When</th><th className="pb-2 pr-3">User</th>
                <th className="pb-2 pr-3">Role</th><th className="pb-2 pr-3">Matched by</th><th className="pb-2">Type</th>
              </tr>
            </thead>
            <tbody>{events.map((e) => <EventRow key={e.id} e={e} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AutojoinActivityLog;
